import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Inbox, MessageSquare, RefreshCw, Check, AlertCircle } from 'lucide-react';
import { messageService, campaignService } from '../lib/supabaseServices';

const SENTIMENT = {
  positive: { label: 'Positive', cls: 'bg-green-500/15 text-green-500 border-green-500/30' },
  neutral: { label: 'Neutral', cls: 'bg-gray-500/15 text-gray-400 border-gray-500/30' },
  negative: { label: 'Negative', cls: 'bg-red-500/15 text-red-500 border-red-500/30' },
};

function timeAgo(ts) {
  if (!ts) return '';
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const InboxPage = ({ darkMode }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState('');
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const card = darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200';
  const sub = darkMode ? 'text-secondary' : 'text-gray-600';
  const head = darkMode ? 'text-primary' : 'text-gray-900';

  const loadResponses = useCallback(async (id) => {
    if (!id) { setResponses([]); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      setResponses(await messageService.getReceivedResponses(id));
    } catch (e) {
      setError(e.message || 'Failed to load replies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const c = await campaignService.getCampaigns();
        setCampaigns(c || []);
        if (c && c.length) { setCampaignId(c[0].id); loadResponses(c[0].id); }
        else setLoading(false);
      } catch (e) {
        setError(e.message || 'Failed to load campaigns'); setLoading(false);
      }
    })();
  }, [loadResponses]);

  const onSelect = (id) => { setCampaignId(id); loadResponses(id); };

  const markRead = async (id) => {
    setResponses((r) => r.map((x) => (x.id === id ? { ...x, is_read: true } : x)));
    try { await messageService.markResponseAsRead(id); } catch (_) { loadResponses(campaignId); }
  };

  const unread = responses.filter((r) => !r.is_read).length;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-4 lg:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-r from-accent to-accent-hover rounded-full flex items-center justify-center">
            <Inbox className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className={`text-xl font-bold ${head}`}>Inbox</h2>
            <p className={`text-sm ${sub}`}>{unread} unread {unread === 1 ? 'reply' : 'replies'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={campaignId}
            onChange={(e) => onSelect(e.target.value)}
            className={`text-sm rounded border px-3 py-2 ${card} ${head}`}
          >
            {campaigns.length === 0 && <option value="">No campaigns</option>}
            {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name || 'Untitled campaign'}</option>)}
          </select>
          <button
            onClick={() => loadResponses(campaignId)}
            className={`p-2 rounded border ${card} ${sub} hover:text-accent`}
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-500 bg-red-500/10 border border-red-500/30 rounded px-3 py-2 mb-4">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* List */}
      {loading ? (
        <p className={`text-sm ${sub} py-10 text-center`}>Loading replies…</p>
      ) : responses.length === 0 ? (
        <div className={`${card} border rounded p-10 text-center`}>
          <MessageSquare className={`w-8 h-8 mx-auto mb-3 ${sub}`} />
          <p className={`font-medium ${head} mb-1`}>No replies yet</p>
          <p className={`text-sm ${sub}`}>Replies from your leads will appear here as the extension detects them.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {responses.map((r) => {
            const s = SENTIMENT[r.response_sentiment] || null;
            return (
              <motion.div
                key={r.id}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className={`${card} border rounded p-4 ${!r.is_read ? 'ring-1 ring-accent/40' : ''}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {!r.is_read && <span className="w-2 h-2 rounded-full bg-accent flex-shrink-0" />}
                      <span className={`font-semibold ${head}`}>
                        {r.lead_username ? `@${r.lead_username}` : 'Unknown lead'}
                      </span>
                      {r.ab_test_variant && (
                        <span className={`text-xs ${sub}`}>variant {r.ab_test_variant}</span>
                      )}
                      {s && <span className={`text-[11px] px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>}
                    </div>
                    <p className={`mt-1 text-sm ${head} break-words`}>{r.message_text}</p>
                    <p className={`mt-1 text-xs ${sub}`}>
                      {timeAgo(r.received_at)}
                      {r.instagram_accounts?.username && ` · via @${r.instagram_accounts.username}`}
                    </p>
                  </div>
                  {!r.is_read && (
                    <button
                      onClick={() => markRead(r.id)}
                      className="flex items-center gap-1 text-xs text-accent hover:text-accent-hover flex-shrink-0"
                      title="Mark as read"
                    >
                      <Check className="w-4 h-4" /> Read
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};

export default InboxPage;
